import useAppPages from "integrations/nextjs/routing/useAppPages"
import store from "integrations/redux/store"
import { useState, type JSX } from "react"
import { hasAuthenticationCookies } from "services/authentication/shared/tools"
import { useLinkedUser } from "user/registration/useLinkedUser"
import { refreshCredentials } from "./thunks"
import { SystemInitializingContainer } from "themes/default-tailwind/panels"
import { useAppSelector } from "integrations/redux/hooks"
import { selectIsAuthenticated } from "./selectors"
import { get } from "nodash"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"

type AuthenticationProviderProps = {
    children: JSX.Element
}

export default function AuthenticationProvider({ children }: AuthenticationProviderProps) {
    const {
        isLoginPage,
        isVerificationPage,
        isRegistrationPage,
        redirectToLoginPage,
        redirectToRootPage,
    } = useAppPages()
    const { linkedEmail } = useLinkedUser()
    const [didRunEffect, setDidRunEffect] = useState(false)
    const isAuthenticated = useAppSelector(selectIsAuthenticated)

    useEffectUnsafe(() => {
        const refreshCreds = async () => {
            const canContinueSession = hasAuthenticationCookies()
            try {
                if (!canContinueSession && !isAuthenticated) throw new Error()
                await store.dispatch(refreshCredentials({ linkedEmail })).unwrap()
                if (isLoginPage || isVerificationPage || isRegistrationPage) {
                    if (localStorage.getItem("isFirstLogin") === "true") {
                        localStorage.setItem("needsToMakeDemoProject", "true")
                    }
                    redirectToRootPage()
                }
            } catch (e) {
                if (get(e, "name") === "ConditionError") return
                if (!isLoginPage && !isVerificationPage && !isRegistrationPage) {
                    redirectToLoginPage()
                }
            } finally {
                setDidRunEffect(true)
            }
        }
        refreshCreds()
    }, [isAuthenticated])

    if (!didRunEffect) return <SystemInitializingContainer />
    return children
}
